<template>
  <div class="quantity-input">
    <span
      class="quantity-remove"
      :class="{
        'opacity-30 !cursor-default': item.quantity === 1,
      }"
      @click.prevent="quantityRemove(itemIndex)">
      <i class="fa-solid fa-minus"></i>
    </span>
    <span class="quantity-value">
      {{ item.quantity }}
    </span>
    <span
      class="quantity-add"
      :class="{
        'opacity-30 !cursor-default': item.max_quantity === item.quantity,
      }"
      @click.prevent="quantityAdd(itemIndex)">
      <i class="fa-solid fa-plus"></i>
    </span>
  </div>
</template>

<script lang="ts" setup>
import { useCartStore } from '~/store/cart'
// @ts-ignore
import { useDebounceFn } from '@vueuse/core'
import isValidResponse from '~/utils/isValidResponse'
import { useLanguageStore } from '~/store/language'
import type { CartQuantityResponseInterface } from '~/types/cart/cartBackendTypes'
import { fetchRetryParameters, requestHostHeaders, type RequestHostHeadersType } from '~/composables/constants/api'
import type { CartItemInterface } from '~/types/cartTypes'

const { itemIndex } = defineProps({
  itemIndex: {
    type: Number,
    required: true,
  },
})

const cartStore = useCartStore()

const item = computed(() => cartStore.items[itemIndex] as CartItemInterface)

async function quantityRemove(idx: number) {
  cartStore.quantityRemove(idx)
  await debounceUpdateQuantity()
}

async function quantityAdd(idx: number) {
  cartStore.quantityAdd(idx)
  await debounceUpdateQuantity()
}

const debounceUpdateQuantity = useDebounceFn(
  () => {
    updateQuantity()
  },
  300,
  { maxWait: 3000 }
)

async function updateQuantity() {
  interface formDataInterface {
    item_id: number
    quantity: number
    language_id: number | null
  }

  const formData: formDataInterface = {
    item_id: cartStore.items[itemIndex].id,
    quantity: cartStore.items[itemIndex].quantity,
    language_id: useLanguageStore().getLanguageId,
  }

  const response = await $fetch<CartQuantityResponseInterface>('/api/cart/quantity', {
    method: 'POST',
    body: formData,
    ...fetchRetryParameters,
    retry: 2,
    headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
  })

  if (!isValidResponse(response)) {
    await cartStore.fetch()
  } else {
    cartStore.setDelivery(response.delivery)
    cartStore.setShipping(response.shipping)
    cartStore.setExpiration(response.expiration)
  }
}
</script>

<style lang="postcss" scoped>
.quantity-input {
  @apply flex items-center h-10 border rounded-[1.875rem] border-gray3 alt:rounded-sm;
  @apply dark:border-dmGray3;

  .quantity-remove {
    @apply text-lg font-black w-[2.5rem] text-center text-gray5 dark:text-dmText1 h-full flex items-center justify-center cursor-pointer;
  }

  .quantity-value {
    @apply text-base font-bold w-[1.875rem] text-center text-black dark:text-white;
  }

  .quantity-add {
    @apply text-lg font-black w-[2.5rem] text-center text-gray5 dark:text-dmText1 h-full flex items-center justify-center cursor-pointer;
  }
}
</style>
